import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, MenuItem,
  Tooltip, CircularProgress, InputAdornment, Pagination, TableCollapse,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArticleIcon from '@mui/icons-material/Article';
import SearchIcon from '@mui/icons-material/Search';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, getDocs, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import PaymentDialog from '../components/PaymentDialog';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { db } from '../firebase';
import { registrarVentaOrden } from '../services/ventaService';

import { COLORS } from '../theme/constants';
import { IMAGING_STATUS_CONFIG, PRIORITY_CONFIG, MODALITY_CONFIG, calcImagingTAT } from '../models/imagingModel';

const { primary: IMG_PRIMARY, teal: IMG_TEAL } = COLORS;
const PAGE_SIZE = 15;

const PAYMENT_STATUS = {
  pagado:    { label: 'Pagado',    color: '#059669', bg: '#D1FAE5' },
  no_pagado: { label: 'No pagado', color: '#DC2626', bg: '#FEE2E2' },
};

// El radiólogo solo informa estudios ya realizados y pagados (las aprobadas ya están cerradas)
const RADIOLOGO_STATUSES = ['imaging_done', 'pending_report', 'draft_report'];

export default function ImagingOrders() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const { activeBranchId, isAllBranches, filterByBranch } = useBranch();
  const isGestion = (userData?.role || '').toLowerCase() === 'gestion';
  const isTecnologo = (userData?.role || '') === 'Tecnólogo';
  const isRadiologo = (userData?.role || '') === 'Radiologo';
  const isMedico = ['médico veterinario', 'director médico'].includes((userData?.role || '').toLowerCase());
  
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payingOrder, setPayingOrder] = useState(null);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || '');
  const [filterPriority, setFilterPriority] = useState(searchParams.get('priority') || '');
  const [filterModality, setFilterModality] = useState('');
  const [filterPayment, setFilterPayment] = useState('');
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [patientsMap, setPatientsMap] = useState({});

  useEffect(() => {
    const constraints = [orderBy('requestedAt', 'desc')];
    if (!isAllBranches && activeBranchId) constraints.unshift(where('branchId', '==', activeBranchId));
    const unsub = onSnapshot(
      query(collection(db, 'imagingOrders'), ...constraints),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOrders(isAllBranches ? filterByBranch(all, { includeUnassigned: true }) : all);
        setLoading(false);
      },
      (err) => {
        console.error('Error loading orders:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [activeBranchId, isAllBranches]);

  useEffect(() => {
    getDocs(collection(db, 'patients')).then((snap) => {
      const map = {};
      snap.docs.forEach((d) => { map[d.id] = d.data().branchIds || []; });
      setPatientsMap(map);
    }).catch(() => {});
  }, []);

  const isAdmin = userData?.role === 'admin';
  const userBranchIds = userData?.branchIds || [];

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return orders.filter((o) => {
      // El radiólogo solo ve estudios con imágenes listas y ya pagados
      if (isRadiologo) {
        if (!RADIOLOGO_STATUSES.includes(o.status)) return false;
        if (o.paymentStatus !== 'pagado') return false;
      }
      // Las órdenes aprobadas ya están cerradas: se ocultan de la lista de trabajo,
      // salvo que se filtre explícitamente por ese estado.
      if (o.status === 'approved' && filterStatus !== 'approved') return false;
      if (filterStatus && o.status !== filterStatus) return false;
      if (filterPriority && o.priority !== filterPriority) return false;
      if (filterModality && !o.studiesRequested?.some(s => s.modality === filterModality)) return false;
      if (filterPayment && o.paymentStatus !== filterPayment) return false;

      // Filter by patient branchIds
      if (!isAdmin && userBranchIds.length > 0 && o.patientId) {
        const patientBranchIds = patientsMap[o.patientId] || [];
        if (patientBranchIds.length > 0 && !patientBranchIds.some(id => userBranchIds.includes(id))) return false;
      }

      if (q && !(
        (o.patientName || '').toLowerCase().includes(q) ||
        (o.orderNumber || '').toLowerCase().includes(q) ||
        (o.vetName || '').toLowerCase().includes(q) ||
        (o.studiesRequested || []).some((s) => s.examTypeName?.toLowerCase().includes(q))
      )) return false;
      return true;
    });
  }, [orders, search, filterStatus, filterPriority, filterModality, filterPayment, patientsMap, userBranchIds, isAdmin, isRadiologo]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const handleExportCSV = () => {
    const headers = ['N° Orden', 'Fecha', 'Paciente', 'Especie', 'Estudio', 'Modalidad', 'Médico Solicita', 'Estado', 'Prioridad', 'TAT (h)', 'Pago'];
    const rows = filtered.map(o => {
      const date = o.requestedAt?.toDate ? o.requestedAt.toDate().toLocaleDateString('es-CL') : '—';
      const studies = o.studiesRequested?.map(s => s.examTypeName).join('; ') || '—';
      const modalities = o.studiesRequested?.map(s => s.modality).join('; ') || '—';
      const tat = calcImagingTAT(o);
      return [
        o.orderNumber || '—',
        date,
        o.patientName || '—',
        o.patientSpecies || '—',
        studies,
        modalities,
        o.vetName || '—',
        IMAGING_STATUS_CONFIG[o.status]?.label || o.status,
        PRIORITY_CONFIG[o.priority]?.label || o.priority,
        tat,
        PAYMENT_STATUS[o.paymentStatus]?.label || o.paymentStatus,
      ];
    });

    const csvContent = [
      '\ufeff' + headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `imagenes_${new Date().toISOString().split('T')[0].replace(/-/g, '')}.csv`;
    link.click();
  };

  const handlePay = async ({ paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes }) => {
    await registrarVentaOrden({
      presupuestoId: payingOrder.presupuestoId || null,
      orderId: payingOrder.id,
      orderCollection: 'imagingOrders',
      orderType: 'imagingOrder',
      orderNumber: payingOrder.orderNumber,
      patientId: payingOrder.patientId || '',
      patientName: payingOrder.patientName || '',
      patientSpecies: payingOrder.patientSpecies || '',
      ownerId: payingOrder.ownerId || '',
      ownerName: payingOrder.ownerName || '',
      fallbackItems: (payingOrder.studiesRequested || []).map((s) => ({
        nombre: s.examTypeName, cantidad: 1,
        precioUnitario: Math.round((s.precioNeto || 0) * 1.19),
        tipoPrestacion: 'Imágenes', unidadNegocio: 'Imágenes',
      })),
      pago: { paymentMethod, boletaNumber, voucherNumber, last4Digits, paymentNotes },
      branchId: payingOrder.branchId || activeBranchId || userData?.branchId || '',
      user: { uid: currentUser?.uid, name: userData?.name, email: currentUser?.email },
    });
    setPaymentOpen(false);
    setPayingOrder(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BrokenImageIcon sx={{ color: IMG_PRIMARY, fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color={IMG_PRIMARY}>Órdenes de Imagen</Typography>
            <Typography variant="caption" color="text.secondary">
              {isRadiologo
                ? 'Estudios con imágenes listas y pago confirmado, pendientes de informe'
                : 'Gestión de estudios radiológicos'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<AddIcon />}
            onClick={() => navigate('/imaging/new')}
            sx={{ borderColor: IMG_PRIMARY, color: IMG_PRIMARY, fontWeight: 600 }}>
            Nueva Orden
          </Button>
          <Button variant="outlined" startIcon={<ArticleIcon />}
            onClick={handleExportCSV}
            sx={{ borderColor: '#64748B', color: '#64748B', fontWeight: 600 }}>
            Exportar CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar paciente, orden, modalidad..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ minWidth: 250 }}
          />
          <TextField
            size="small"
            select
            label="Estado"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            sx={{ minWidth: 150 }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todos</MenuItem>
            {Object.entries(IMAGING_STATUS_CONFIG)
              .filter(([key]) => !isRadiologo || RADIOLOGO_STATUSES.includes(key))
              .map(([key, cfg]) => (
                <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
              ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Prioridad"
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            sx={{ minWidth: 120 }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Modalidad"
            value={filterModality}
            onChange={(e) => setFilterModality(e.target.value)}
            sx={{ minWidth: 120 }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">Todas</MenuItem>
            {Object.entries(MODALITY_CONFIG).map(([key, cfg]) => (
              <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
            ))}
          </TextField>
          {isGestion && (
            <TextField
              size="small"
              select
              label="Pago"
              value={filterPayment}
              onChange={(e) => setFilterPayment(e.target.value)}
              sx={{ minWidth: 120 }}
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(PAYMENT_STATUS).map(([key, cfg]) => (
                <MenuItem key={key} value={key}>{cfg.label}</MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                <TableCell />
                <TableCell sx={{ fontWeight: 700 }}>N°</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Paciente</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estudios</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Modalidad</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Prioridad</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>TAT</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Archivos</TableCell>
                {isGestion && <TableCell sx={{ fontWeight: 700 }}>Pago</TableCell>}
                <TableCell sx={{ fontWeight: 700 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isGestion ? 11 : 10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No se encontraron órdenes
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((order) => (
                  <React.Fragment key={order.id}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => setExpandedRow(expandedRow === order.id ? null : order.id)}>
                      <TableCell padding="none">
                        <IconButton size="small">
                          {expandedRow === order.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{order.orderNumber || '—'}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>{order.patientName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">{order.patientSpecies || '—'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{order.studiesRequested?.length || 0}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {order.studiesRequested?.slice(0, 2).map((s, idx) => (
                            <Chip key={idx} label={s.modality} size="small" sx={{ fontSize: '0.65rem', bgcolor: MODALITY_CONFIG[s.modality]?.bg, color: MODALITY_CONFIG[s.modality]?.color }} />
                          ))}
                          {order.studiesRequested?.length > 2 && (
                            <Chip label={`+${order.studiesRequested.length - 2}`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#F1F5F9', color: '#64748B' }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={PRIORITY_CONFIG[order.priority]?.label || order.priority} size="small" sx={{ fontSize: '0.65rem', bgcolor: PRIORITY_CONFIG[order.priority]?.bg, color: PRIORITY_CONFIG[order.priority]?.color }} />
                      </TableCell>
                      <TableCell>
                        <Chip label={IMAGING_STATUS_CONFIG[order.status]?.label || order.status} size="small" sx={{ fontSize: '0.65rem', bgcolor: IMAGING_STATUS_CONFIG[order.status]?.bg, color: IMAGING_STATUS_CONFIG[order.status]?.color }} />
                      </TableCell>
                      <TableCell>{calcImagingTAT(order)} h</TableCell>
                      <TableCell>
                        {order.files?.length > 0 ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ImageOutlinedIcon sx={{ fontSize: 16, color: '#10B981' }} />
                            <Typography variant="caption" color="#10B981" fontWeight={600}>{order.files.length}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.disabled">Sin imágenes</Typography>
                        )}
                      </TableCell>
                      {isGestion && (
                        <TableCell>
                          <Chip label={PAYMENT_STATUS[order.paymentStatus]?.label || order.paymentStatus} size="small" sx={{ fontSize: '0.65rem', bgcolor: PAYMENT_STATUS[order.paymentStatus]?.bg, color: PAYMENT_STATUS[order.paymentStatus]?.color }} />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Ver detalle">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/imaging/orders/${order.id}`); }}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {order.status === 'pending_imaging' || order.status === 'imaging_done' ? (
                            <Tooltip title="Subir imágenes">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/imaging/orders/${order.id}`); }}>
                                <UploadFileIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {order.status === 'imaging_done' || order.status === 'pending_report'
                            || (isRadiologo && RADIOLOGO_STATUSES.includes(order.status)) ? (
                            <Tooltip title="Generar informe">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/imaging/report/${order.id}`); }}>
                                <ArticleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {isGestion && order.paymentStatus === 'no_pagado' && (order.status === 'approved' || order.status === 'reported') ? (
                            <Tooltip title="Cobrar">
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); setPayingOrder(order); setPaymentOpen(true); }}>
                                <AttachMoneyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                    {expandedRow === order.id && (
                      <TableRow>
                        <TableCell colSpan={isGestion ? 11 : 10} sx={{ bgcolor: '#F8FAFC', p: 2 }}>
                          <Box sx={{ pl: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} mb={1}>Estudios solicitados:</Typography>
                            {order.studiesRequested?.map((study, idx) => (
                              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip label={study.modality} size="small" sx={{ fontSize: '0.6rem', bgcolor: MODALITY_CONFIG[study.modality]?.bg, color: MODALITY_CONFIG[study.modality]?.color }} />
                                <Typography variant="body2">{study.examTypeName}</Typography>
                                {study.sedationRequired && <Chip label="Sedación" size="small" sx={{ fontSize: '0.6rem', bgcolor: '#FEF3C7', color: '#92400E' }} />}
                                {study.contrastRequired && <Chip label={`Contraste: ${study.contrastType}`} size="small" sx={{ fontSize: '0.6rem', bgcolor: '#DBEAFE', color: '#1E40AF' }} />}
                              </Box>
                            ))}
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Indicación: {order.clinicalIndication || '—'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" />
          </Box>
        )}
      </Paper>

      <PaymentDialog open={paymentOpen} onClose={() => { setPaymentOpen(false); setPayingOrder(null); }} onPay={handlePay} />
    </Box>
  );
}
